import { useMemo, useState } from "react";
import {
  getOpDef,
  listOpsByFamily,
  validateTransformChain,
  type TransformFamily,
  type TransformOpDef,
} from "@capture/shared";
import { TransformStepConfig } from "../form-config/export/components/TransformStepConfig";
import { usePublishedTransformPlugins } from "../form-config/export/hooks/usePublishedTransformPlugins";
import { groupPluginOpsByFamily, filterPluginOps, type PluginPickerOp } from "../form-config/export/lib/plugin-ops";
import type { TransformPluginRow } from "../transform-plugins/api/transform-plugins-client";

interface Step {
  type: string;
  config: Record<string, unknown>;
}

interface StepsEditorProps {
  value: Step[];
  onChange: (next: Step[]) => void;
}

const FAMILY_ORDER: TransformFamily[] = [
  "string",
  "literal",
  "cross-field",
  "logic",
  "lookup",
  "number",
  "date",
];

const FAMILY_LABEL: Record<TransformFamily, string> = {
  string: "String ops",
  literal: "Literal ops",
  "cross-field": "Cross-field",
  logic: "Logic",
  lookup: "Lookup",
  number: "Number",
  date: "Date",
};

export function StepsEditor({ value, onChange }: StepsEditorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { plugins } = usePublishedTransformPlugins();

  const pluginOpIds = useMemo(() => new Set(plugins.map((p) => p.pluginId)), [plugins]);
  const validation = useMemo(
    () => validateTransformChain(value, { knownExternalOpIds: pluginOpIds }),
    [value, pluginOpIds],
  );
  const errorsByStep = useMemo(() => {
    const map: Record<number, Array<{ field: string; message: string }>> = {};
    if (!validation.ok) {
      for (const e of validation.errors) {
        (map[e.stepIndex] ??= []).push({ field: e.field, message: e.message });
      }
    }
    return map;
  }, [validation]);

  function addOp(opDef: TransformOpDef | PluginPickerOp) {
    onChange([...value, { type: opDef.id, config: { ...opDef.defaultConfig } }]);
    setPickerOpen(false);
  }

  function removeStep(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function updateConfig(index: number, config: Record<string, unknown>) {
    onChange(value.map((s, i) => (i === index ? { ...s, config } : s)));
  }

  function moveStep(from: number, to: number) {
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    const [item] = next.splice(from, 1);
    if (item) next.splice(to, 0, item);
    onChange(next);
  }

  return (
    <div className="rb-section">
      <h2 className="rb-section-title">Steps Panel</h2>

      {value.length === 0 ? (
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">
            No steps yet — click &quot;Add transform&quot; to pick one.
          </span>
        </div>
      ) : (
        <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {value.map((step, index) => {
            const opDef = getOpDef(step.type);
            return (
              <li
                key={index}
                style={{
                  border: "1px solid var(--color-border, #2a2a2a)",
                  borderRadius: 4,
                  padding: 8,
                  marginBottom: 8,
                  background: "var(--color-bg-elevated, #161616)",
                }}
              >
                <header
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <span
                    className="rb-name"
                    style={{ fontSize: "var(--text-xs)" }}
                  >
                    Step {index + 1} — {opDef?.label ?? step.type}
                  </span>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      className="rb-btn rb-btn-cancel"
                      aria-label={`Move ${opDef?.label ?? step.type} up`}
                      onClick={() => moveStep(index, index - 1)}
                      disabled={index === 0}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="rb-btn rb-btn-cancel"
                      aria-label={`Move ${opDef?.label ?? step.type} down`}
                      onClick={() => moveStep(index, index + 1)}
                      disabled={index === value.length - 1}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="rb-btn rb-btn-danger"
                      aria-label={`Remove ${opDef?.label ?? step.type}`}
                      onClick={() => removeStep(index)}
                    >
                      Remove
                    </button>
                  </div>
                </header>
                <TransformStepConfig
                  opId={step.type}
                  config={step.config}
                  onChange={(config) => updateConfig(index, config)}
                  errors={errorsByStep[index] ?? []}
                  plugins={plugins}
                />
              </li>
            );
          })}
        </ol>
      )}

      <div className="rb-row">
        {pickerOpen ? (
          <OpPicker onPick={addOp} onCancel={() => setPickerOpen(false)} plugins={plugins} />
        ) : (
          <button
            type="button"
            className="rb-btn rb-btn-save"
            onClick={() => setPickerOpen(true)}
          >
            + Add transform
          </button>
        )}
      </div>
    </div>
  );
}

function OpPicker({
  onPick,
  onCancel,
  plugins = [],
}: {
  onPick: (op: TransformOpDef | PluginPickerOp) => void;
  onCancel: () => void;
  plugins?: TransformPluginRow[];
}) {
  const [search, setSearch] = useState("");
  const families = listOpsByFamily();
  const query = search.toLowerCase();

  // F-043 — merge published tenant plugins into their declared family group,
  // badged "custom", mirroring TransformChainDrawer's picker. Memoized on
  // `plugins` (not `search`) so it isn't rebuilt on every keystroke.
  const pluginsByFamily = useMemo(() => groupPluginOpsByFamily(plugins), [plugins]);

  return (
    <div
      role="listbox"
      aria-label="Transform op picker"
      style={{
        width: "100%",
        border: "1px solid var(--color-border, #2a2a2a)",
        borderRadius: 4,
        padding: 8,
        background: "var(--color-bg-elevated, #161616)",
      }}
    >
      <div className="rb-row" style={{ marginBottom: 8 }}>
        <input
          autoFocus
          type="search"
          className="rb-input"
          placeholder="Search transforms…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search transforms"
        />
        <button
          type="button"
          className="rb-btn rb-btn-cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {FAMILY_ORDER.map((fam) => {
          const ops = (families[fam] ?? []).filter(
            (op) =>
              !query ||
              op.id.toLowerCase().includes(query) ||
              op.label.toLowerCase().includes(query),
          );
          const pluginOps = filterPluginOps(pluginsByFamily[fam] ?? [], query);
          if (ops.length === 0 && pluginOps.length === 0) return null;
          return (
            <li key={fam} style={{ marginBottom: 8 }}>
              <div
                style={{
                  fontSize: "var(--text-2xs)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--color-text-muted, #525252)",
                  marginBottom: 4,
                }}
              >
                {FAMILY_LABEL[fam]}
              </div>
              {ops.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => onPick(op)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "6px 8px",
                    margin: "2px 0",
                    background: "transparent",
                    border: "1px solid var(--color-border, #2a2a2a)",
                    borderRadius: 4,
                    color: "var(--color-text-primary, #e5e5e5)",
                    fontSize: "var(--text-xs)",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 500 }}>{op.label}</div>
                  <div
                    style={{
                      fontSize: "var(--text-2xs)",
                      color: "var(--color-text-muted, #525252)",
                      marginTop: 2,
                    }}
                  >
                    {op.description}
                  </div>
                </button>
              ))}
              {pluginOps.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  aria-label={`${op.label} (custom)`}
                  onClick={() => onPick(op)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "6px 8px",
                    margin: "2px 0",
                    background: "transparent",
                    border: "1px solid var(--color-border, #2a2a2a)",
                    borderRadius: 4,
                    color: "var(--color-text-primary, #e5e5e5)",
                    fontSize: "var(--text-xs)",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 500 }}>
                    {op.label}{" "}
                    <span
                      style={{
                        fontSize: "var(--text-2xs)",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        fontWeight: 600,
                        color: "var(--color-accent, #c27040)",
                        border: "1px solid var(--color-accent, #c27040)",
                        borderRadius: 3,
                        padding: "0 4px",
                      }}
                    >
                      custom
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "var(--text-2xs)",
                      color: "var(--color-text-muted, #525252)",
                      marginTop: 2,
                    }}
                  >
                    {op.description}
                  </div>
                </button>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
